import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { StytchConfigContext } from "@/contexts/StytchConfigContext";
import type { StytchConfig } from "@/contexts/StytchConfigContext";
import { pathNeedsStytch } from "@/utils/stytchRoutes";
import { hasStytchSessionCookie } from "@/hooks/useAuth";
import {
  preloadPublicJobData,
  preloadPublicPageChunk,
} from "@/utils/publicPages";
import App, { preloadStytchProviders } from "./App";
import "./index.css";

/** What the app runs on before (or without) a config from the backend. */
const DISABLED_STYTCH_CONFIG: StytchConfig = {
  enabled: false,
  environment: 'development',
  consumerPublicToken: undefined,
  b2bPublicToken: undefined,
};

// Replit dev banner — only loaded in development, stripped from production bundle
if (import.meta.env.DEV) {
  const devBanner = document.createElement("script");
  devBanner.async = true;
  devBanner.type = "text/javascript";
  devBanner.src = "https://replit.com/public/js/replit-dev-banner.js";
  document.body.appendChild(devBanner);
}

import("@sentry/react").then((Sentry) => {
  Sentry.init({
    dsn: "https://aeda888736fa9f0bda56abd445faeadd@o4511178502569984.ingest.de.sentry.io/4511178563190864",
    sendDefaultPii: false,
  });
}).catch((err) => {
  console.warn('[Sentry] Failed to initialize:', err);
});

if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (console) {
        console.warn('Cannot remove child: not a child of this node', child, this);
      }
      return child;
    }
    return originalRemoveChild.apply(this, [child]) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) {
        console.warn('Cannot insert before: reference node is not a child of this node', referenceNode, this);
      }
      return newNode;
    }
    return originalInsertBefore.apply(this, [newNode, referenceNode]) as T;
  };
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    __talenture_root?: Root;
    __talenture_initPromise?: Promise<void>;
  }
}

// HMR-aware global storage pattern: Store React root on window to survive module reloads
// This prevents "createRoot() called twice" errors during Vite hot module replacement
function getOrCreateRoot(element: HTMLElement): Root {
  if (!window.__talenture_root) {
    console.log('[Talenture Init] Creating new React root');
    window.__talenture_root = createRoot(element);
  } else {
    console.log('[Talenture Init] Reusing existing React root');
  }
  return window.__talenture_root;
}

/**
 * Fetch Stytch configuration from backend at runtime
 * 
 * IMPORTANT: Token Selection Architecture
 * ----------------------------------------
 * The frontend does NOT select tokens based on VITE_IS_PRODUCTION or environment variables.
 * Instead, we use a runtime configuration pattern:
 * 
 * 1. Backend detects environment via IS_PRODUCTION (checks REPLIT_DEPLOYMENT=1)
 * 2. Backend selects appropriate Stytch PUBLIC tokens (TEST vs LIVE)
 * 3. Frontend fetches tokens via this endpoint at startup
 * 4. Frontend uses whatever tokens the backend provides
 * 
 * This ensures:
 * - Single source of truth for environment detection (backend only)
 * - No environment variable mismatches between frontend and backend
 * - Tokens automatically match the backend's secret keys
 * - Frontend never needs to know about deployment environment
 */
async function fetchStytchConfig(): Promise<StytchConfig> {
  try {
    const response = await fetch('/api/auth/stytch-config');
    if (!response.ok) {
      throw new Error(`Failed to fetch Stytch config: ${response.status}`);
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('[Stytch Init] Failed to fetch config:', error);
    // Return safe defaults - disabled
    return DISABLED_STYTCH_CONFIG;
  }
}

// Initialize app.
//
// The Stytch SDK and its providers are NO LONGER mounted here. Mounting them
// eagerly pulled the full auth SDK (@stytch/react + @stytch/vanilla-js, both
// consumer and b2b) into the main entry chunk, so every public marketing page
// paid the cost on first load. Instead, the providers are mounted lazily by
// StytchProviderGate (see App.tsx), which loads the SDK only on auth/app routes.
// Marketing pages never download it.
//
// We still fetch the runtime Stytch config here and expose it via
// StytchConfigContext so the lazy provider boundary (and components that read the
// config, e.g. SignupStep) have it available synchronously once they mount.
async function initializeApp() {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  // Get or create root using global storage (HMR-safe)
  const root = getOrCreateRoot(rootElement);

  // Public pages are served with their content already in the HTML, and React
  // replaces that content the moment it renders. Waiting for the Stytch config
  // round trip before rendering means the page a visitor is already reading
  // blanks out until the response lands. On routes that cannot need the auth
  // SDK, render first and take the config when it arrives.
  //
  // Every other route still waits: the lazy provider boundary decides whether
  // Stytch is on from the route and the session cookie, and a component that
  // calls a Stytch hook before its provider exists throws.
  const canRenderBeforeConfig =
    !pathNeedsStytch(window.location.pathname) && !hasStytchSessionCookie();

  // On a prerendered public route whose page component is a lazy chunk,
  // resolve that chunk BEFORE the first render — regardless of session state,
  // because a logged-in visitor on /jobs or a legal page sees the same
  // prerendered copy and would get the same flash. The download starts now so
  // it runs in parallel with the Stytch config fetch below; the prerendered
  // copy stays on screen while it downloads, and once it lands React's first
  // commit is the real page instead of the route-level "Loading page..."
  // spinner.
  //
  // The waits below are NOT capped by a timeout on purpose. A slow-but-
  // eventually-loading chunk must never release the render early — that would
  // commit the Suspense spinner over the prerendered copy, the exact flash
  // this exists to prevent. The prerendered content stays readable the whole
  // time. And the wait cannot hang forever: preload() settles on BOTH success
  // and failure (a failed import resolves after being swallowed), at which
  // point we render and the lazy path's chunk-error recovery (one-shot page
  // reload) takes over.
  const publicLocation = window.location.pathname + window.location.search;
  const chunkReady = preloadPublicPageChunk(publicLocation);
  const publicJobDataReady = preloadPublicJobData(publicLocation);

  // When the first render will mount the Stytch provider boundary (session
  // cookie present, or an auth/app route), start that chunk download now too —
  // in parallel with the config fetch — so the gate's Suspense fallback never
  // paints over a prerendered page for a logged-in visitor.
  const providersReady = canRenderBeforeConfig ? null : preloadStytchProviders();

  if (canRenderBeforeConfig) {
    if (chunkReady) await chunkReady;
    if (publicJobDataReady) await publicJobDataReady;

    root.render(
      <StytchConfigContext.Provider value={DISABLED_STYTCH_CONFIG}>
        <App />
      </StytchConfigContext.Provider>
    );
  }

  // Fetch Stytch configuration from backend at runtime
  const stytchConfig = await fetchStytchConfig();

  // On the session-cookie path the first render happens below, so the page
  // chunk and the Stytch provider chunk must both be ready HERE. The config
  // fetch above ran in parallel with those downloads, so this usually
  // resolves immediately.
  if (!canRenderBeforeConfig) {
    if (chunkReady) await chunkReady;
    if (publicJobDataReady) await publicJobDataReady;
    if (providersReady) await providersReady;
  }

  console.log('[Stytch Init] Configuration:', {
    enabled: stytchConfig.enabled,
    environment: stytchConfig.environment,
    hasConsumerToken: !!stytchConfig.consumerPublicToken,
    hasB2BToken: !!stytchConfig.b2bPublicToken,
  });

  root.render(
    <StytchConfigContext.Provider value={stytchConfig}>
      <App />
    </StytchConfigContext.Provider>
  );
}

// Prevent concurrent initialization during HMR
if (window.__talenture_initPromise) {
  console.log('[Talenture Init] Initialization already in progress, waiting...');
} else {
  window.__talenture_initPromise = initializeApp().catch(error => {
    console.error('[Stytch Init] Fatal error:', error);
    // Fallback: render app without Stytch providers but with disabled config context
    const rootElement = document.getElementById("root");
    if (rootElement) {
      // Use global root (HMR-safe)
      const root = getOrCreateRoot(rootElement);
      root.render(
        <StytchConfigContext.Provider value={DISABLED_STYTCH_CONFIG}>
          <App />
        </StytchConfigContext.Provider>
      );
    }
  });
}

// HMR cleanup: Clear init promise when module is disposed
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    console.log('[Talenture Init] HMR dispose - clearing init promise');
    window.__talenture_initPromise = undefined;
  });
}
